import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, DatePicker, Flex, Form, Row, Select, Spin, Tooltip } from "antd";
import { FileExcelOutlined, FolderOpenOutlined, PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { setTitle } from "../../../redux/slices/configuracoes/configuracoesSlice";
import { resetRelatorioConsolidadoSaldoBancario, setBuscaConsolidadoSaldoBancario } from "../../../redux/slices/relatorio/relatorioSlice";
import { buscarTodasEmpresa } from "../../../services/empresaService";
import { gerarRelatorioConsolidadoSaldoBancario, gerarRelatorioConsolidadoSaldoBancarioDetalhe, gerarRelatorioConsolidadoSaldoBancarioResumo } from "../../../services/relatorioService";
import { BuscarRelatorioConsolidadoSaldoBancarioModel } from "../../../models/relatorio/buscarRelatorioConsolidadoSaldoBancarioModel";
import { RelatorioConsolidadoSaldoBancarioModel } from "../../../models/relatorio/relatorioConsolidadoSaldoBancarioModel";
import { RelatorioConsolidadoSaldoBancarioDetalheModel } from "../../../models/relatorio/relatorioConsolidadoSaldoBancarioDetalheModel";
import { RelatorioConsolidadoSaldoBancarioResumoModel } from "../../../models/relatorio/relatorioConsolidadoSaldoBancarioResumoModel";
import { ListaEmpresaDTOModel } from "../../../models/empresa/listaEmpresaDTOModel";
import { ComposicaoSalvaModel } from "../../../models/composicao/composicaoSalvaModel";
import ModalComposicoesSalvas from "../../../components/composicoesSalvas/modalComposicoesSalvas";
import { currencyBRL } from "../../../util/dinheiroBRAuxiliar";
import dayjs from "dayjs";

const CAT_EMPRESA_DA_SORTE = "Empresa Dá Sorte";
const CAT_4WAYS = "4Ways/Promobem";
const CAT_TECNOLOGIA = "Empresa de Tecnologia";
const CAT_INTERM_NEGOCIOS = "Interm. Negocios";

const SONHO_DA_SORTE_ID = 64;

//empresas da seção "Sonho da Sorte" da tela de Composição de Resultado (mesma regra de lá)
const SONHO_RESULTADO_IDS = [64, 65];


const ConsolidadoSaldoBancario = () => {
  const dispatch = useAppDispatch();
  const empresaState = useAppSelector((state) => state.empresa);
  const relatorioState = useAppSelector((state) => state.relatorio);
  const busca = relatorioState.buscaConsolidadoSaldoBancario;

  const empresasSelecionadas = busca.listaEmpresa;
  const [form] = Form.useForm();
  const relatorioPrintRef = useRef<HTMLDivElement>(null);
  const [modalComposicoesAberto, setModalComposicoesAberto] = useState(false);

  const setEmpresasSelecionadas = (ids: number[]) => {
    dispatch(setBuscaConsolidadoSaldoBancario({ ...busca, listaEmpresa: ids }));
    //resultados na tela deixam de refletir os filtros - limpa até o próximo GERAR
    dispatch(resetRelatorioConsolidadoSaldoBancario());
  };

  //preenche empresas e período do relatório a partir de uma composição de resultado salva,
  //garantindo o match da seção Resumo (mesma chave: período + conjunto de empresas)
  const usarComposicaoSalva = (item: ComposicaoSalvaModel) => {
    const ids = item.listaEmpresasAssinatura.split("|").filter(Boolean).map(Number);
    dispatch(
      setBuscaConsolidadoSaldoBancario({
        ...busca,
        listaEmpresa: ids,
        dataInicio: dayjs(item.dataInicio).toDate(),
        dataFim: dayjs(item.dataFim).toDate(),
      })
    );
    form.setFieldsValue({ dataInicio: dayjs(item.dataInicio), dataFim: dayjs(item.dataFim) });
    dispatch(resetRelatorioConsolidadoSaldoBancario());
    setModalComposicoesAberto(false);
  };

  useEffect(() => {
    dispatch(setTitle("RELATÓRIO CONSOLIDADO DE SALDOS BANCÁRIOS"));
    dispatch(buscarTodasEmpresa(null));
    form.setFieldsValue({
      dataInicio: busca.dataInicio ? dayjs(busca.dataInicio) : null,
      dataFim: busca.dataFim ? dayjs(busca.dataFim) : null,
    });
  }, []);

  const onChangeDateInicio = (value: any) => {
    const novaBusca: BuscarRelatorioConsolidadoSaldoBancarioModel = { ...busca, dataInicio: value ? dayjs(value).toDate() : null };
    dispatch(setBuscaConsolidadoSaldoBancario(novaBusca));
    dispatch(resetRelatorioConsolidadoSaldoBancario());
  };

  const onChangeDateFim = (value: any) => {
    const novaBusca: BuscarRelatorioConsolidadoSaldoBancarioModel = { ...busca, dataFim: value ? dayjs(value).toDate() : null };
    dispatch(setBuscaConsolidadoSaldoBancario(novaBusca));
    dispatch(resetRelatorioConsolidadoSaldoBancario());
  };

  const empresaMap = useMemo(() => {
    const map: Record<number, ListaEmpresaDTOModel> = {};
    empresaState.listAll.forEach((e) => {
      map[e.id!] = e;
    });
    return map;
  }, [empresaState.listAll]);

  const empresasFiltradas = useMemo(() => {
    return empresasSelecionadas.map((id) => empresaMap[id]).filter(Boolean);
  }, [empresasSelecionadas, empresaMap]);

  // Separar empresas em 3 blocos
  const bloco1Empresas = useMemo(() => {
    return empresasFiltradas.filter((e) => e.categoriaEmpresa?.trim() === CAT_EMPRESA_DA_SORTE && e.id !== SONHO_DA_SORTE_ID);
  }, [empresasFiltradas]);

  const bloco2Empresas = useMemo(() => {
    return empresasFiltradas.filter(
      (e) => e.categoriaEmpresa?.trim() === CAT_4WAYS || e.categoriaEmpresa?.trim() === CAT_TECNOLOGIA || e.id === SONHO_DA_SORTE_ID
    );
  }, [empresasFiltradas]);

  const bloco2Empresas4Ways = useMemo(() => {
    return empresasFiltradas.filter((e) => e.categoriaEmpresa?.trim() === CAT_4WAYS);
  }, [empresasFiltradas]);

  const bloco2EmpresasTecnologia = useMemo(() => {
    return empresasFiltradas.filter((e) => e.categoriaEmpresa?.trim() === CAT_TECNOLOGIA);
  }, [empresasFiltradas]);

  const bloco2EmpresasSonho = useMemo(() => {
    return empresasFiltradas.filter((e) => e.id === SONHO_DA_SORTE_ID);
  }, [empresasFiltradas]);

  const bloco3Empresas = useMemo(() => {
    return empresasFiltradas.filter((e) => e.categoriaEmpresa?.trim() === CAT_INTERM_NEGOCIOS);
  }, [empresasFiltradas]);

  const gerarRelatorio = () => {
    if (!busca.dataInicio || !busca.dataFim || empresasSelecionadas.length === 0) return;

    const idsBloco12 = [...bloco1Empresas, ...bloco2Empresas].map((e) => e.id!);
    const idsBloco3 = bloco3Empresas.map((e) => e.id!);

    const payload = {
      tipoDataFiltro: "AUTO",
      dataInicio: dayjs(busca.dataInicio).format("YYYY-MM-DD"),
      dataFim: dayjs(busca.dataFim).format("YYYY-MM-DD"),
      realizado: true,
      naoRealizado: true,
      vencido: true,
      filtros: busca.filtros,
    };

    if (idsBloco12.length > 0) {
      dispatch(
        gerarRelatorioConsolidadoSaldoBancario({
          ...payload,
          listaEmpresa: idsBloco12,
        })
      );
    }

    //Resumo recebe a seleção COMPLETA (não só blocos 1+2): a composição salva tem como chave
    //o conjunto exato de empresas selecionado - excluir o bloco 3 quebraria o match
    dispatch(
      gerarRelatorioConsolidadoSaldoBancarioResumo({
        ...payload,
        listaEmpresa: empresasSelecionadas,
      })
    );

    if (idsBloco3.length > 0) {
      dispatch(
        gerarRelatorioConsolidadoSaldoBancarioDetalhe({
          ...payload,
          tipoDataFiltro: "LANC",
          listaEmpresa: idsBloco3,
        })
      );
    }
  };

  const dados: RelatorioConsolidadoSaldoBancarioModel[] = relatorioState.listConsolidadoSaldoBancario;
  const dadosDetalhe: RelatorioConsolidadoSaldoBancarioDetalheModel[] = useMemo(() => {
    return relatorioState.listConsolidadoSaldoBancarioDetalhe.filter((d) => d.denominacaoContaBancaria?.startsWith("P4 -"));
  }, [relatorioState.listConsolidadoSaldoBancarioDetalhe]);
  const dadosResumo: RelatorioConsolidadoSaldoBancarioResumoModel[] = relatorioState.listConsolidadoSaldoBancarioResumo;
  const resumoSemComposicao = dadosResumo.length > 0 && dadosResumo.every((r) => r.idFechamentoComposicaoResultado === null);

  // Agrupamento do Resumo no front, mesmas regras dos blocos do relatório:
  // Cap's = Empresa Dá Sorte (sem Sonho da Sorte) | SS = Sonho da Sorte
  //Espelha as duas seções da tela de Composição de Resultado:
  //Cap's = somatório de todas as empresas (tabela principal) | SS = seção Sonho da Sorte (64/65)
  const resumoBlocos = useMemo(() => {
    if (dadosResumo.length === 0) return [];
    const somarBloco = (nome: string, filtro: (item: RelatorioConsolidadoSaldoBancarioResumoModel) => boolean) => {
      const itens = dadosResumo.filter(filtro);
      return {
        nome,
        resultadoApurado: itens.reduce((a, r) => a + r.resultadoApurado, 0),
        caucaoVariacao: itens.reduce((a, r) => a + r.caucaoVariacao, 0),
        aporteGarantiaVariacao: itens.reduce((a, r) => a + r.aporteGarantiaVariacao, 0),
        lucroReal: itens.reduce((a, r) => a + r.lucroReal, 0),
      };
    };
    return [
      somarBloco("Cap's", (r) => !SONHO_RESULTADO_IDS.includes(r.idEmpresa)),
      somarBloco("SS", (r) => SONHO_RESULTADO_IDS.includes(r.idEmpresa)),
    ];
  }, [dadosResumo]);

  const gruposOrdenados = useMemo(() => {
    const grupoMap: Record<number, { denominacao: string; ordem: number }> = {};
    dados.forEach((d) => {
      if (!grupoMap[d.idGrupoContaBanco]) {
        grupoMap[d.idGrupoContaBanco] = {
          denominacao: d.denominacaoGrupoContaBanco,
          ordem: d.ordemGrupoContaBanco,
        };
      }
    });
    return Object.entries(grupoMap)
      .map(([id, info]) => ({ id: Number(id), ...info }))
      .sort((a, b) => a.ordem - b.ordem);
  }, [dados]);

  const dadosIndexados = useMemo(() => {
    const idx: Record<number, Record<number, RelatorioConsolidadoSaldoBancarioModel>> = {};
    dados.forEach((d) => {
      if (!idx[d.idGrupoContaBanco]) idx[d.idGrupoContaBanco] = {};
      idx[d.idGrupoContaBanco][d.idEmpresa] = d;
    });
    return idx;
  }, [dados]);

  // Totais do consolidado (blocos 1 + 2)
  const totalConsolidado = useMemo(() => {
    let mesAtual = 0;
    let mesAnterior = 0;
    let variacao = 0;
    dados.forEach((d) => {
      mesAtual += d.saldoMesAtual;
      mesAnterior += d.saldoMesAnterior;
      variacao += d.variacao;
    });
    return { mesAtual, mesAnterior, variacao };
  }, [dados]);

  // Totais do detalhe (bloco 3)
  const totalDetalhe = useMemo(() => {
    let mesAtual = 0;
    let mesAnterior = 0;
    let variacao = 0;
    dadosDetalhe.forEach((d) => {
      mesAtual += d.saldoMesAtual;
      mesAnterior += d.saldoMesAnterior;
      variacao += d.variacao;
    });
    return { mesAtual, mesAnterior, variacao };
  }, [dadosDetalhe]);

  // Total geral (consolidado + detalhe)
  const totalGeral = useMemo(() => ({
    mesAtual: totalConsolidado.mesAtual + totalDetalhe.mesAtual,
    mesAnterior: totalConsolidado.mesAnterior + totalDetalhe.mesAnterior,
    variacao: totalConsolidado.variacao + totalDetalhe.variacao,
  }), [totalConsolidado, totalDetalhe]);

  const nomeEmpresa = (emp: ListaEmpresaDTOModel) => emp.apelido || emp.razaoSocial;

  const formatValue = (value: number) => currencyBRL(value);

  const cellStyle = (value: number): React.CSSProperties => ({
    textAlign: "right",
    padding: "6px 8px",
    color: value < 0 ? "#ef4444" : undefined,
    whiteSpace: "nowrap",
  });

  const headerStyle: React.CSSProperties = {
    padding: "8px",
    textAlign: "center",
    fontWeight: "bold",
    backgroundColor: "#f1f5f9",
    whiteSpace: "nowrap",
    fontSize: 12,
  };

  const isLoading = relatorioState.loadingConsolidadoSaldoBancario || relatorioState.loadingConsolidadoSaldoBancarioDetalhe || relatorioState.loadingConsolidadoSaldoBancarioResumo;
  const temDados = dados.length > 0 || dadosDetalhe.length > 0;

  // --- Excel export ---
  const handleExcel = () => {
    const data: any[] = [];

    data.push({ Grupo: `Relatório Consolidado de Saldos Bancários - ${`${dayjs(busca.dataInicio).format("DD/MM/YYYY")} a ${dayjs(busca.dataFim).format("DD/MM/YYYY")}`}` });
    data.push({});

    // Bloco 1 (Empresa Dá Sorte)
    if (bloco1Empresas.length > 0) {
      const empresas = bloco1Empresas;
      const empresaIds = empresas.map((e) => e.id!);

      data.push({ Grupo: CAT_EMPRESA_DA_SORTE });

      const headerRow: any = { Grupo: "Grupo" };
      empresas.forEach((emp) => { headerRow[nomeEmpresa(emp)] = nomeEmpresa(emp); });
      headerRow["Total"] = "Total";
      headerRow["Mês Anterior"] = "Mês Anterior";
      headerRow["Variação"] = "Variação";
      data.push(headerRow);

      const totalPorEmpresa: Record<number, { mesAtual: number; mesAnterior: number; variacao: number }> = {};
      empresaIds.forEach((id) => { totalPorEmpresa[id] = { mesAtual: 0, mesAnterior: 0, variacao: 0 }; });

      gruposOrdenados.forEach((grupo) => {
        const row: any = { Grupo: grupo.denominacao };
        let tma = 0, tman = 0, tv = 0;
        empresas.forEach((emp) => {
          const item = dadosIndexados[grupo.id]?.[emp.id!];
          const valor = item?.saldoMesAtual ?? 0;
          row[nomeEmpresa(emp)] = valor;
          if (item) { tma += item.saldoMesAtual; tman += item.saldoMesAnterior; tv += item.variacao; totalPorEmpresa[emp.id!].mesAtual += item.saldoMesAtual; totalPorEmpresa[emp.id!].mesAnterior += item.saldoMesAnterior; totalPorEmpresa[emp.id!].variacao += item.variacao; }
        });
        row["Total"] = tma; row["Mês Anterior"] = tman; row["Variação"] = tv;
        data.push(row);
      });

      const totalRow: any = { Grupo: "TOTAL" };
      const tgMa = Object.values(totalPorEmpresa).reduce((a, v) => a + v.mesAtual, 0);
      const tgMan = Object.values(totalPorEmpresa).reduce((a, v) => a + v.mesAnterior, 0);
      const tgV = Object.values(totalPorEmpresa).reduce((a, v) => a + v.variacao, 0);
      empresas.forEach((emp) => { totalRow[nomeEmpresa(emp)] = totalPorEmpresa[emp.id!]?.mesAtual ?? 0; });
      totalRow["Total"] = tgMa; totalRow["Mês Anterior"] = tgMan; totalRow["Variação"] = tgV;
      data.push(totalRow);
      data.push({});
    }

    // Bloco 2, dividido em duas seções: (1) 4 Ways e (2) Empresa de Tecnologia / Sonho da Sorte
    const addBlocoExcel = (titulo: string, grupos: { empresas: ListaEmpresaDTOModel[]; suf: string }[]) => {
      const gruposValidos = grupos.filter((g) => g.empresas.length > 0);
      if (gruposValidos.length === 0) return;

      data.push({ Grupo: titulo });

      const headerRow: any = { Grupo: "Grupo" };
      gruposValidos.forEach((g) => {
        g.empresas.forEach((emp) => { headerRow[nomeEmpresa(emp)] = nomeEmpresa(emp); });
        headerRow[`Total ${g.suf}`] = "Total";
        headerRow[`Mês Ant. ${g.suf}`] = "Mês Anterior";
        headerRow[`Var. ${g.suf}`] = "Variação";
      });
      data.push(headerRow);

      const totalPorEmpresa: Record<number, { mesAtual: number; mesAnterior: number; variacao: number }> = {};
      gruposValidos.flatMap((g) => g.empresas).forEach((e) => { totalPorEmpresa[e.id!] = { mesAtual: 0, mesAnterior: 0, variacao: 0 }; });

      gruposOrdenados.forEach((grupo) => {
        const row: any = { Grupo: grupo.denominacao };
        gruposValidos.forEach((g) => {
          let ma = 0, man = 0, v = 0;
          g.empresas.forEach((emp) => {
            const item = dadosIndexados[grupo.id]?.[emp.id!];
            row[nomeEmpresa(emp)] = item?.saldoMesAtual ?? 0;
            if (item) { ma += item.saldoMesAtual; man += item.saldoMesAnterior; v += item.variacao; totalPorEmpresa[emp.id!].mesAtual += item.saldoMesAtual; totalPorEmpresa[emp.id!].mesAnterior += item.saldoMesAnterior; totalPorEmpresa[emp.id!].variacao += item.variacao; }
          });
          row[`Total ${g.suf}`] = ma; row[`Mês Ant. ${g.suf}`] = man; row[`Var. ${g.suf}`] = v;
        });
        data.push(row);
      });

      const totalRow: any = { Grupo: "TOTAL" };
      gruposValidos.forEach((g) => {
        let gma = 0, gman = 0, gv = 0;
        g.empresas.forEach((emp) => {
          const t = totalPorEmpresa[emp.id!];
          totalRow[nomeEmpresa(emp)] = t?.mesAtual ?? 0;
          gma += t?.mesAtual ?? 0; gman += t?.mesAnterior ?? 0; gv += t?.variacao ?? 0;
        });
        totalRow[`Total ${g.suf}`] = gma; totalRow[`Mês Ant. ${g.suf}`] = gman; totalRow[`Var. ${g.suf}`] = gv;
      });
      data.push(totalRow);
      data.push({});
    };

    addBlocoExcel("4 Ways", [{ empresas: bloco2Empresas4Ways, suf: "4Ways" }]);
    addBlocoExcel("Empresa de Tecnologia / Sonho da Sorte", [
      { empresas: bloco2EmpresasTecnologia, suf: "Tec" },
      { empresas: bloco2EmpresasSonho, suf: "Sonho" },
    ]);

    // Bloco 3 (P4 - contas bancárias individuais)
    if (dadosDetalhe.length > 0) {
      data.push({ Grupo: CAT_INTERM_NEGOCIOS });

      const headerRowDetalhe: any = {
        Grupo: "Conta Bancária",
        "Total": "Saldo Mês Atual",
        "Mês Anterior": "Saldo Mês Anterior",
        "Variação": "Variação",
      };
      data.push(headerRowDetalhe);

      dadosDetalhe.forEach((d) => {
        data.push({
          Grupo: d.denominacaoContaBancaria,
          "Total": d.saldoMesAtual,
          "Mês Anterior": d.saldoMesAnterior,
          "Variação": d.variacao,
        });
      });

      data.push({
        Grupo: "TOTAL",
        "Total": totalDetalhe.mesAtual,
        "Mês Anterior": totalDetalhe.mesAnterior,
        "Variação": totalDetalhe.variacao,
      });

      data.push({});
    }

    // Total geral
    data.push({ Grupo: "TOTAL GERAL", Total: totalGeral.mesAtual, "Mês Anterior": totalGeral.mesAnterior, "Variação": totalGeral.variacao });

    const ws = XLSX.utils.json_to_sheet(data);

    // Resumo (composição de resultado salva): em colunas à direita, abaixo do Total Geral
    if (resumoBlocos.length > 0) {
      const mesReferencia = busca.dataInicio ? dayjs(busca.dataInicio).format("MMM/YYYY") : "";
      const linhasResumo: (string | number)[][] = [["Lucro"]];
      resumoBlocos.forEach((r) => {
        linhasResumo.push([`Resultado Apurado - ${mesReferencia}`, r.resultadoApurado]);
        linhasResumo.push(["Caução - Variação", r.caucaoVariacao]);
        linhasResumo.push(["Aporte Garantia - Variação", r.aporteGarantiaVariacao]);
        linhasResumo.push([`Lucro Real ${r.nome}`, r.lucroReal]);
      });
      const range = XLSX.utils.decode_range(ws["!ref"] as string);
      const colResumo = Math.max(range.e.c - 1, 0); // bloco ocupa as duas colunas mais à direita
      const rowResumo = data.length + 2; // header(1) + linhas + 1 linha em branco abaixo do Total Geral
      XLSX.utils.sheet_add_aoa(ws, linhasResumo, { origin: { r: rowResumo, c: colResumo } });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Consolidado");
    XLSX.writeFile(wb, `ConsolidadoSaldoBancario_${dayjs(busca.dataInicio).format("YYYY-MM-DD")}.xlsx`);
  };

  // --- PDF (print) ---
  const handlePrint = useReactToPrint({
    content: () => relatorioPrintRef.current,
  });

  // --- Render table per category (blocos 1 e 2) ---
  const renderTabela = (titulo: string, empresasCat: ListaEmpresaDTOModel[]) => {
    const empresaIds = empresasCat.map((e) => e.id!);

    const totalPorEmpresa: Record<number, { mesAtual: number; mesAnterior: number; variacao: number }> = {};
    empresaIds.forEach((id) => {
      totalPorEmpresa[id] = { mesAtual: 0, mesAnterior: 0, variacao: 0 };
    });

    gruposOrdenados.forEach((grupo) => {
      empresaIds.forEach((empId) => {
        const item = dadosIndexados[grupo.id]?.[empId];
        if (item) {
          totalPorEmpresa[empId].mesAtual += item.saldoMesAtual;
          totalPorEmpresa[empId].mesAnterior += item.saldoMesAnterior;
          totalPorEmpresa[empId].variacao += item.variacao;
        }
      });
    });

    const totalGeralMesAtual = Object.values(totalPorEmpresa).reduce((acc, v) => acc + v.mesAtual, 0);
    const totalGeralMesAnterior = Object.values(totalPorEmpresa).reduce((acc, v) => acc + v.mesAnterior, 0);
    const totalGeralVariacao = Object.values(totalPorEmpresa).reduce((acc, v) => acc + v.variacao, 0);

    return (
      <div key={titulo} style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 8, color: "#334155" }}>{titulo}</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: "left", minWidth: 180 }}>GRUPO</th>
                {empresasCat.map((emp) => (
                  <th key={emp.id} style={{ ...headerStyle, minWidth: 130 }}>
                    {nomeEmpresa(emp)}
                  </th>
                ))}
                <th style={{ ...headerStyle, minWidth: 130, backgroundColor: "#e2e8f0" }}>TOTAL</th>
                <th style={{ ...headerStyle, minWidth: 130 }}>MÊS ANTERIOR</th>
                <th style={{ ...headerStyle, minWidth: 130 }}>VARIAÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {gruposOrdenados.map((grupo) => {
                let totalGrupoMesAtual = 0;
                let totalGrupoMesAnterior = 0;
                let totalGrupoVariacao = 0;

                empresaIds.forEach((empId) => {
                  const item = dadosIndexados[grupo.id]?.[empId];
                  if (item) {
                    totalGrupoMesAtual += item.saldoMesAtual;
                    totalGrupoMesAnterior += item.saldoMesAnterior;
                    totalGrupoVariacao += item.variacao;
                  }
                });

                return (
                  <tr key={grupo.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 500 }}>{grupo.denominacao}</td>
                    {empresasCat.map((emp) => {
                      const item = dadosIndexados[grupo.id]?.[emp.id!];
                      const valor = item?.saldoMesAtual ?? 0;
                      return (
                        <td key={emp.id} style={cellStyle(valor)}>
                          {formatValue(valor)}
                        </td>
                      );
                    })}
                    <td style={{ ...cellStyle(totalGrupoMesAtual), fontWeight: "bold", backgroundColor: "#f8fafc" }}>
                      {formatValue(totalGrupoMesAtual)}
                    </td>
                    <td style={cellStyle(totalGrupoMesAnterior)}>{formatValue(totalGrupoMesAnterior)}</td>
                    <td
                      style={{
                        ...cellStyle(totalGrupoVariacao),
                        color: totalGrupoVariacao > 0 ? "#22c55e" : totalGrupoVariacao < 0 ? "#ef4444" : undefined,
                      }}
                    >
                      {formatValue(totalGrupoVariacao)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: "2px solid #334155", fontWeight: "bold" }}>
                <td style={{ padding: "8px", fontWeight: "bold" }}>TOTAL</td>
                {empresasCat.map((emp) => {
                  const valor = totalPorEmpresa[emp.id!]?.mesAtual ?? 0;
                  return (
                    <td key={emp.id} style={{ ...cellStyle(valor), fontWeight: "bold" }}>
                      {formatValue(valor)}
                    </td>
                  );
                })}
                <td style={{ ...cellStyle(totalGeralMesAtual), fontWeight: "bold", backgroundColor: "#fef08a" }}>
                  {formatValue(totalGeralMesAtual)}
                </td>
                <td style={{ ...cellStyle(totalGeralMesAnterior), fontWeight: "bold", backgroundColor: "#fef08a" }}>
                  {formatValue(totalGeralMesAnterior)}
                </td>
                <td
                  style={{
                    ...cellStyle(totalGeralVariacao),
                    fontWeight: "bold",
                    backgroundColor: "#fef08a",
                    color: totalGeralVariacao > 0 ? "#22c55e" : totalGeralVariacao < 0 ? "#ef4444" : undefined,
                  }}
                >
                  {formatValue(totalGeralVariacao)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // --- Render genérico: tabela com 1+ grupos de empresas, cada grupo com seu subtotal ---
  const renderTabelaGrupos = (titulo: string, grupos: ListaEmpresaDTOModel[][]) => {
    const gruposValidos = grupos.filter((g) => g.length > 0);
    if (gruposValidos.length === 0) return null;

    const todasEmpresas = gruposValidos.flat();
    const totalPorEmpresa: Record<number, { mesAtual: number; mesAnterior: number; variacao: number }> = {};
    todasEmpresas.forEach((e) => { totalPorEmpresa[e.id!] = { mesAtual: 0, mesAnterior: 0, variacao: 0 }; });
    gruposOrdenados.forEach((grupo) => {
      todasEmpresas.forEach((e) => {
        const item = dadosIndexados[grupo.id]?.[e.id!];
        if (item) { totalPorEmpresa[e.id!].mesAtual += item.saldoMesAtual; totalPorEmpresa[e.id!].mesAnterior += item.saldoMesAnterior; totalPorEmpresa[e.id!].variacao += item.variacao; }
      });
    });
    const totalGrupo = (g: ListaEmpresaDTOModel[]) =>
      g.reduce(
        (a, e) => ({
          mesAtual: a.mesAtual + (totalPorEmpresa[e.id!]?.mesAtual ?? 0),
          mesAnterior: a.mesAnterior + (totalPorEmpresa[e.id!]?.mesAnterior ?? 0),
          variacao: a.variacao + (totalPorEmpresa[e.id!]?.variacao ?? 0),
        }),
        { mesAtual: 0, mesAnterior: 0, variacao: 0 }
      );
    const sep = "3px solid #94a3b8";

    return (
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 8, color: "#334155" }}>{titulo}</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: "left", minWidth: 180 }}>GRUPO</th>
                {gruposValidos.map((g, gi) => (
                  <React.Fragment key={gi}>
                    {g.map((emp, idx) => (
                      <th key={emp.id} style={{ ...headerStyle, minWidth: 130, borderLeft: gi > 0 && idx === 0 ? sep : undefined }}>{nomeEmpresa(emp)}</th>
                    ))}
                    <th style={{ ...headerStyle, minWidth: 130, backgroundColor: "#e2e8f0" }}>TOTAL</th>
                    <th style={{ ...headerStyle, minWidth: 130 }}>MÊS ANTERIOR</th>
                    <th style={{ ...headerStyle, minWidth: 130 }}>VARIAÇÃO</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {gruposOrdenados.map((grupo) => (
                <tr key={grupo.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "6px 8px", fontWeight: 500 }}>{grupo.denominacao}</td>
                  {gruposValidos.map((g, gi) => {
                    let ma = 0, man = 0, v = 0;
                    g.forEach((emp) => { const item = dadosIndexados[grupo.id]?.[emp.id!]; if (item) { ma += item.saldoMesAtual; man += item.saldoMesAnterior; v += item.variacao; } });
                    return (
                      <React.Fragment key={gi}>
                        {g.map((emp, idx) => {
                          const valor = dadosIndexados[grupo.id]?.[emp.id!]?.saldoMesAtual ?? 0;
                          return <td key={emp.id} style={{ ...cellStyle(valor), borderLeft: gi > 0 && idx === 0 ? sep : undefined }}>{formatValue(valor)}</td>;
                        })}
                        <td style={{ ...cellStyle(ma), fontWeight: "bold", backgroundColor: "#f8fafc" }}>{formatValue(ma)}</td>
                        <td style={cellStyle(man)}>{formatValue(man)}</td>
                        <td style={{ ...cellStyle(v), color: v > 0 ? "#22c55e" : v < 0 ? "#ef4444" : undefined }}>{formatValue(v)}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #334155", fontWeight: "bold" }}>
                <td style={{ padding: "8px", fontWeight: "bold" }}>TOTAL</td>
                {gruposValidos.map((g, gi) => {
                  const gt = totalGrupo(g);
                  return (
                    <React.Fragment key={gi}>
                      {g.map((emp, idx) => {
                        const valor = totalPorEmpresa[emp.id!]?.mesAtual ?? 0;
                        return <td key={emp.id} style={{ ...cellStyle(valor), fontWeight: "bold", borderLeft: gi > 0 && idx === 0 ? sep : undefined }}>{formatValue(valor)}</td>;
                      })}
                      <td style={{ ...cellStyle(gt.mesAtual), fontWeight: "bold", backgroundColor: "#fef08a" }}>{formatValue(gt.mesAtual)}</td>
                      <td style={{ ...cellStyle(gt.mesAnterior), fontWeight: "bold", backgroundColor: "#fef08a" }}>{formatValue(gt.mesAnterior)}</td>
                      <td style={{ ...cellStyle(gt.variacao), fontWeight: "bold", backgroundColor: "#fef08a", color: gt.variacao > 0 ? "#22c55e" : gt.variacao < 0 ? "#ef4444" : undefined }}>{formatValue(gt.variacao)}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // --- Render table detalhe (bloco 3 - P4 contas bancárias individuais) ---
  const renderTabelaDetalhe = () => {
    if (dadosDetalhe.length === 0) return null;

    return (
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 8, color: "#334155" }}>{CAT_INTERM_NEGOCIOS}</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: "left", minWidth: 250 }}>CONTA BANCÁRIA</th>
                <th style={{ ...headerStyle, minWidth: 150, backgroundColor: "#e2e8f0" }}>SALDO MÊS ATUAL</th>
                <th style={{ ...headerStyle, minWidth: 150 }}>MÊS ANTERIOR</th>
                <th style={{ ...headerStyle, minWidth: 150 }}>VARIAÇÃO</th>
              </tr>
            </thead>
            <tbody>
              {dadosDetalhe.map((d) => (
                <tr key={d.idContaBanco} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "6px 8px", fontWeight: 500 }}>{d.denominacaoContaBancaria}</td>
                  <td style={{ ...cellStyle(d.saldoMesAtual), fontWeight: "bold", backgroundColor: "#f8fafc" }}>
                    {formatValue(d.saldoMesAtual)}
                  </td>
                  <td style={cellStyle(d.saldoMesAnterior)}>{formatValue(d.saldoMesAnterior)}</td>
                  <td
                    style={{
                      ...cellStyle(d.variacao),
                      color: d.variacao > 0 ? "#22c55e" : d.variacao < 0 ? "#ef4444" : undefined,
                    }}
                  >
                    {formatValue(d.variacao)}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #334155", fontWeight: "bold" }}>
                <td style={{ padding: "8px", fontWeight: "bold" }}>TOTAL</td>
                <td style={{ ...cellStyle(totalDetalhe.mesAtual), fontWeight: "bold", backgroundColor: "#fef08a" }}>
                  {formatValue(totalDetalhe.mesAtual)}
                </td>
                <td style={{ ...cellStyle(totalDetalhe.mesAnterior), fontWeight: "bold", backgroundColor: "#fef08a" }}>
                  {formatValue(totalDetalhe.mesAnterior)}
                </td>
                <td
                  style={{
                    ...cellStyle(totalDetalhe.variacao),
                    fontWeight: "bold",
                    backgroundColor: "#fef08a",
                    color: totalDetalhe.variacao > 0 ? "#22c55e" : totalDetalhe.variacao < 0 ? "#ef4444" : undefined,
                  }}
                >
                  {formatValue(totalDetalhe.variacao)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // --- Render seção Resumo (layout padrão do cliente: tabela vertical "Lucro") ---
  const renderResumo = (compacto: boolean = false) => {
    if (resumoBlocos.length === 0) return null;

    const mesReferencia = busca.dataInicio ? dayjs(busca.dataInicio).format("MMM/YYYY") : "";
    const linhas: { label: string; valor: number; total?: boolean }[] = resumoBlocos.flatMap((bloco) => [
      { label: `Resultado Apurado - ${mesReferencia}`, valor: bloco.resultadoApurado },
      { label: "Caução - Variação", valor: bloco.caucaoVariacao },
      { label: "Aporte Garantia - Variação", valor: bloco.aporteGarantiaVariacao },
      { label: `Lucro Real ${bloco.nome}`, valor: bloco.lucroReal, total: true },
    ]);

    const borda = compacto ? "1px solid #ccc" : "1px solid #e2e8f0";
    const celula: React.CSSProperties = { padding: compacto ? 4 : "6px 8px", fontSize: compacto ? 11 : 13 };

    return (
      <div style={{ marginBottom: compacto ? 20 : 24, pageBreakInside: "avoid", maxWidth: 480 }}>
        {resumoSemComposicao && (
          <p style={{ fontSize: 12, color: "#b45309", marginBottom: 8 }}>
            Nenhuma composição de resultado salva foi encontrada para estas empresas e período — o Resultado Apurado está zerado. Salve a composição na tela "Composição de Resultado da Empresa"
            com os mesmos filtros.
          </p>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", border: borda }}>
          <thead>
            <tr>
              <th colSpan={2} style={{ ...celula, textAlign: "center", fontWeight: "bold", backgroundColor: "#f1f5f9", borderBottom: borda }}>
                Lucro
              </th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha, index) => (
              <tr key={index} style={{ borderBottom: borda, backgroundColor: linha.total ? "#f8fafc" : undefined }}>
                <td style={{ ...celula, fontWeight: linha.total ? "bold" : 500, textAlign: linha.total ? "right" : "left" }}>{linha.label}</td>
                <td style={{ ...celula, textAlign: "right", whiteSpace: "nowrap", fontWeight: linha.total ? "bold" : undefined, color: linha.valor < 0 ? "#ef4444" : undefined }}>
                  {formatValue(linha.valor)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // --- Print: render table consolidado ---
  const renderTabelaPrint = (titulo: string, empresasCat: ListaEmpresaDTOModel[]) => {
    const empresaIds = empresasCat.map((e) => e.id!);

    const totalPorEmpresa: Record<number, { mesAtual: number; mesAnterior: number; variacao: number }> = {};
    empresaIds.forEach((id) => {
      totalPorEmpresa[id] = { mesAtual: 0, mesAnterior: 0, variacao: 0 };
    });
    gruposOrdenados.forEach((grupo) => {
      empresaIds.forEach((empId) => {
        const item = dadosIndexados[grupo.id]?.[empId];
        if (item) {
          totalPorEmpresa[empId].mesAtual += item.saldoMesAtual;
          totalPorEmpresa[empId].mesAnterior += item.saldoMesAnterior;
          totalPorEmpresa[empId].variacao += item.variacao;
        }
      });
    });
    const tgMesAtual = Object.values(totalPorEmpresa).reduce((acc, v) => acc + v.mesAtual, 0);
    const tgMesAnterior = Object.values(totalPorEmpresa).reduce((acc, v) => acc + v.mesAnterior, 0);
    const tgVariacao = Object.values(totalPorEmpresa).reduce((acc, v) => acc + v.variacao, 0);

    return (
      <div key={titulo} style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
        <h3 style={{ marginBottom: 6 }}>{titulo}</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "left" }}>Grupo</th>
              {empresasCat.map((emp) => (
                <th key={emp.id} style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>
                  {nomeEmpresa(emp)}
                </th>
              ))}
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>Total</th>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>Mês Anterior</th>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>Variação</th>
            </tr>
          </thead>
          <tbody>
            {gruposOrdenados.map((grupo) => {
              let tma = 0, tman = 0, tv = 0;
              empresaIds.forEach((empId) => {
                const item = dadosIndexados[grupo.id]?.[empId];
                if (item) { tma += item.saldoMesAtual; tman += item.saldoMesAnterior; tv += item.variacao; }
              });
              return (
                <tr key={grupo.id}>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{grupo.denominacao}</td>
                  {empresasCat.map((emp) => {
                    const valor = dadosIndexados[grupo.id]?.[emp.id!]?.saldoMesAtual ?? 0;
                    return (
                      <td key={emp.id} style={{ border: "1px solid #ccc", padding: 4, textAlign: "right", color: valor < 0 ? "#ef4444" : undefined }}>
                        {formatValue(valor)}
                      </td>
                    );
                  })}
                  <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right", fontWeight: "bold" }}>{formatValue(tma)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(tman)}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right", color: tv > 0 ? "#22c55e" : tv < 0 ? "#ef4444" : undefined }}>{formatValue(tv)}</td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: "bold", backgroundColor: "#fef08a" }}>
              <td style={{ border: "1px solid #ccc", padding: 4 }}>TOTAL</td>
              {empresasCat.map((emp) => (
                <td key={emp.id} style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>
                  {formatValue(totalPorEmpresa[emp.id!]?.mesAtual ?? 0)}
                </td>
              ))}
              <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(tgMesAtual)}</td>
              <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(tgMesAnterior)}</td>
              <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(tgVariacao)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // --- Print: render genérico de tabela com 1+ grupos, cada um com seu subtotal ---
  const renderTabelaGruposPrint = (titulo: string, grupos: ListaEmpresaDTOModel[][]) => {
    const gruposValidos = grupos.filter((g) => g.length > 0);
    if (gruposValidos.length === 0) return null;

    const todasEmpresas = gruposValidos.flat();
    const totalPorEmpresa: Record<number, { mesAtual: number; mesAnterior: number; variacao: number }> = {};
    todasEmpresas.forEach((e) => { totalPorEmpresa[e.id!] = { mesAtual: 0, mesAnterior: 0, variacao: 0 }; });
    gruposOrdenados.forEach((grupo) => {
      todasEmpresas.forEach((e) => {
        const item = dadosIndexados[grupo.id]?.[e.id!];
        if (item) { totalPorEmpresa[e.id!].mesAtual += item.saldoMesAtual; totalPorEmpresa[e.id!].mesAnterior += item.saldoMesAnterior; totalPorEmpresa[e.id!].variacao += item.variacao; }
      });
    });
    const totalGrupo = (g: ListaEmpresaDTOModel[]) =>
      g.reduce(
        (a, e) => ({
          mesAtual: a.mesAtual + (totalPorEmpresa[e.id!]?.mesAtual ?? 0),
          mesAnterior: a.mesAnterior + (totalPorEmpresa[e.id!]?.mesAnterior ?? 0),
          variacao: a.variacao + (totalPorEmpresa[e.id!]?.variacao ?? 0),
        }),
        { mesAtual: 0, mesAnterior: 0, variacao: 0 }
      );

    const pcs: React.CSSProperties = { border: "1px solid #ccc", padding: 4, textAlign: "right" };

    return (
      <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
        <h3 style={{ marginBottom: 6 }}>{titulo}</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "left" }}>Grupo</th>
              {gruposValidos.map((g, gi) => (
                <React.Fragment key={gi}>
                  {g.map((emp) => (<th key={emp.id} style={pcs}>{nomeEmpresa(emp)}</th>))}
                  <th style={pcs}>Total</th><th style={pcs}>Mês Anterior</th><th style={pcs}>Variação</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {gruposOrdenados.map((grupo) => (
              <tr key={grupo.id}>
                <td style={{ border: "1px solid #ccc", padding: 4 }}>{grupo.denominacao}</td>
                {gruposValidos.map((g, gi) => {
                  let ma = 0, man = 0, v = 0;
                  g.forEach((emp) => { const item = dadosIndexados[grupo.id]?.[emp.id!]; if (item) { ma += item.saldoMesAtual; man += item.saldoMesAnterior; v += item.variacao; } });
                  return (
                    <React.Fragment key={gi}>
                      {g.map((emp) => { const valor = dadosIndexados[grupo.id]?.[emp.id!]?.saldoMesAtual ?? 0; return <td key={emp.id} style={{ ...pcs, color: valor < 0 ? "#ef4444" : undefined }}>{formatValue(valor)}</td>; })}
                      <td style={{ ...pcs, fontWeight: "bold" }}>{formatValue(ma)}</td><td style={pcs}>{formatValue(man)}</td><td style={{ ...pcs, color: v > 0 ? "#22c55e" : v < 0 ? "#ef4444" : undefined }}>{formatValue(v)}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", backgroundColor: "#fef08a" }}>
              <td style={{ border: "1px solid #ccc", padding: 4 }}>TOTAL</td>
              {gruposValidos.map((g, gi) => {
                const gt = totalGrupo(g);
                return (
                  <React.Fragment key={gi}>
                    {g.map((emp) => <td key={emp.id} style={pcs}>{formatValue(totalPorEmpresa[emp.id!]?.mesAtual ?? 0)}</td>)}
                    <td style={pcs}>{formatValue(gt.mesAtual)}</td><td style={pcs}>{formatValue(gt.mesAnterior)}</td><td style={pcs}>{formatValue(gt.variacao)}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // --- Print: render table detalhe (P4) ---
  const renderTabelaDetalhePrint = () => {
    if (dadosDetalhe.length === 0) return null;

    return (
      <div style={{ marginBottom: 20, pageBreakInside: "avoid" }}>
        <h3 style={{ marginBottom: 6 }}>{CAT_INTERM_NEGOCIOS}</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ccc", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "left" }}>Conta Bancária</th>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>Saldo Mês Atual</th>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>Mês Anterior</th>
              <th style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>Variação</th>
            </tr>
          </thead>
          <tbody>
            {dadosDetalhe.map((d) => (
              <tr key={d.idContaBanco}>
                <td style={{ border: "1px solid #ccc", padding: 4 }}>{d.denominacaoContaBancaria}</td>
                <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right", fontWeight: "bold" }}>{formatValue(d.saldoMesAtual)}</td>
                <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(d.saldoMesAnterior)}</td>
                <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right", color: d.variacao > 0 ? "#22c55e" : d.variacao < 0 ? "#ef4444" : undefined }}>{formatValue(d.variacao)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", backgroundColor: "#fef08a" }}>
              <td style={{ border: "1px solid #ccc", padding: 4 }}>TOTAL</td>
              <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(totalDetalhe.mesAtual)}</td>
              <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(totalDetalhe.mesAnterior)}</td>
              <td style={{ border: "1px solid #ccc", padding: 4, textAlign: "right" }}>{formatValue(totalDetalhe.variacao)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Flex vertical gap={16}>
      <Form form={form} layout="vertical">
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12} wrap="wrap">
              <Form.Item label="Empresas" style={{ flex: 1, minWidth: 300 }}>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Selecione as empresas"
                  value={empresasSelecionadas}
                  onChange={(values) => setEmpresasSelecionadas(values)}
                  loading={empresaState.loadingAll}
                  showSearch
                  optionFilterProp="label"
                  options={empresaState.listAll.map((e) => ({
                    value: e.id!,
                    label: e.apelido || e.razaoSocial,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Data início" name="dataInicio">
                <DatePicker onChange={onChangeDateInicio} format="DD/MM/YYYY" style={{ width: 130 }} />
              </Form.Item>
              <Form.Item label="Data fim" name="dataFim">
                <DatePicker onChange={onChangeDateFim} format="DD/MM/YYYY" style={{ width: 130 }} />
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Flex gap={8} justify="end">
              <Form.Item>
                <Tooltip title="Carregar empresas e período de uma composição salva">
                  <Button icon={<FolderOpenOutlined />} onClick={() => setModalComposicoesAberto(true)} />
                </Tooltip>
              </Form.Item>
              <Form.Item>
                <Button
                  className="buttonPrimary"
                  icon={<SearchOutlined />}
                  onClick={gerarRelatorio}
                  loading={isLoading}
                  disabled={!busca.dataInicio || !busca.dataFim || empresasSelecionadas.length === 0}
                >
                  GERAR
                </Button>
              </Form.Item>
              <Form.Item>
                <Button icon={<PrinterOutlined />} onClick={handlePrint} disabled={!temDados} />
              </Form.Item>
              <Form.Item>
                <Button icon={<FileExcelOutlined />} onClick={handleExcel} disabled={!temDados} />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>

      <span style={{ fontSize: 12, color: "#64748b", marginTop: -8 }}>
        Tipo de data utilizado: <b>Sorteio</b> para empresas da categoria "Empresa Dá Sorte" e <b>Lançamento</b> para as demais.
      </span>

      {isLoading ? (
        <Flex justify="center" style={{ padding: 40 }}>
          <Spin size="large" />
        </Flex>
      ) : (
        temDados && (
          <>
            {/* Bloco 1 - Empresa Dá Sorte */}
            {bloco1Empresas.length > 0 && dados.length > 0 && renderTabela(CAT_EMPRESA_DA_SORTE, bloco1Empresas)}

            {/* Bloco 2a - 4 Ways */}
            {bloco2Empresas4Ways.length > 0 && dados.length > 0 && renderTabelaGrupos("4 Ways", [bloco2Empresas4Ways])}

            {/* Bloco 2b - Empresa de Tecnologia / Sonho da Sorte (subtotais separados) */}
            {(bloco2EmpresasTecnologia.length > 0 || bloco2EmpresasSonho.length > 0) && dados.length > 0 && renderTabelaGrupos("Empresa de Tecnologia / Sonho da Sorte", [bloco2EmpresasTecnologia, bloco2EmpresasSonho])}

            {/* Bloco 3 - P4 / Interm. Negocios */}
            {renderTabelaDetalhe()}

            <div
              style={{
                padding: 16,
                backgroundColor: "#fef08a",
                borderRadius: 4,
                display: "flex",
                justifyContent: "space-between",
                fontWeight: "bold",
                fontSize: 15,
              }}
            >
              <span>TOTAL GERAL</span>
              <Flex gap={32}>
                <span style={{ color: totalGeral.mesAtual < 0 ? "#ef4444" : undefined }}>
                  Mês Atual: {formatValue(totalGeral.mesAtual)}
                </span>
                <span style={{ color: totalGeral.mesAnterior < 0 ? "#ef4444" : undefined }}>
                  Mês Anterior: {formatValue(totalGeral.mesAnterior)}
                </span>
                <span
                  style={{
                    color: totalGeral.variacao > 0 ? "#22c55e" : totalGeral.variacao < 0 ? "#ef4444" : undefined,
                  }}
                >
                  Variação: {formatValue(totalGeral.variacao)}
                </span>
              </Flex>
            </div>

            {/* Resumo - composição de resultado salva + variações Caução/Aporte Garantia (abaixo do Total Geral, à direita) */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>{renderResumo()}</div>
          </>
        )
      )}

      <ModalComposicoesSalvas open={modalComposicoesAberto} onClose={() => setModalComposicoesAberto(false)} onSelecionar={usarComposicaoSalva} textoAcao="Usar" />

      {/* Hidden printable version */}
      <div style={{ display: "none" }}>
        <div ref={relatorioPrintRef} style={{ padding: 24 }}>
          <h2 style={{ textAlign: "center", marginBottom: 4 }}>Relatório Consolidado de Saldos Bancários</h2>
          <p style={{ textAlign: "center", marginBottom: 24, color: "#64748b" }}>
            Referência: {dayjs(busca.dataInicio).format("DD/MM/YYYY")} a {dayjs(busca.dataFim).format("DD/MM/YYYY")} | Gerado em: {dayjs().format("DD/MM/YYYY [às] HH:mm")}
          </p>

          {/* Bloco 1 - Empresa Dá Sorte */}
          {bloco1Empresas.length > 0 && dados.length > 0 && renderTabelaPrint(CAT_EMPRESA_DA_SORTE, bloco1Empresas)}

          {/* Bloco 2a - 4 Ways */}
          {bloco2Empresas4Ways.length > 0 && dados.length > 0 && renderTabelaGruposPrint("4 Ways", [bloco2Empresas4Ways])}

          {/* Bloco 2b - Empresa de Tecnologia / Sonho da Sorte */}
          {(bloco2EmpresasTecnologia.length > 0 || bloco2EmpresasSonho.length > 0) && dados.length > 0 && renderTabelaGruposPrint("Empresa de Tecnologia / Sonho da Sorte", [bloco2EmpresasTecnologia, bloco2EmpresasSonho])}

          {/* Bloco 3 - P4 / Interm. Negocios */}
          {renderTabelaDetalhePrint()}

          <div style={{ padding: 10, backgroundColor: "#fef08a", fontWeight: "bold", fontSize: 13, display: "flex", justifyContent: "space-between" }}>
            <span>TOTAL GERAL</span>
            <span>Mês Atual: {formatValue(totalGeral.mesAtual)} | Mês Anterior: {formatValue(totalGeral.mesAnterior)} | Variação: {formatValue(totalGeral.variacao)}</span>
          </div>

          {/* Resumo - abaixo do Total Geral, alinhado à direita */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>{renderResumo(true)}</div>
        </div>
      </div>
    </Flex>
  );
};

export default ConsolidadoSaldoBancario;
